import Flickity from 'flickity-fade';
import _debounce from 'lodash/debounce';
import {
	getRTL,
	isKeyEvent,
	getBreakpoint,
	onLoadComplete,
	getIndexedHashProperty,
} from './_utils';
import { ytReady } from './youtube';
import triggerAnalyticsEvent, { attachYouTubeAnalyticsEvents } from '../analytics/analytics';
import { attachAnalyticsEvents } from '../analytics/carousel';
import { scrollToElement } from './scrollToQueryHash';

let defaultCarouselClassName = 'InlineCarousel';
let carouselClassName = defaultCarouselClassName;
let overrideOptions = {};
const slideClassName = 'CarouselSlide';
const thirdPartyVideoClassName = 'ThirdPartyVideo';

let indexedHashProperty = getIndexedHashProperty();

const videoAspectRatio = 16 / 9;
// Either playing or buffering
const youtubePlayers = {};
//Slides
let inlineCarouselSlides = [];

let selectors = {};

let classes = {};

const setup = () => {
	selectors = {
		carouselContainer: `.${carouselClassName}--js`,
		carouselList: `.${carouselClassName}-list`,
		carouselSlides: `.${slideClassName}`,
		carouselNextButton: `.${carouselClassName}-nextButton`,
		carouselPrevButton: `.${carouselClassName}-prevButton`,
		thirdPartyVideo: `.${thirdPartyVideoClassName}`,
		equalHeight: '.carousel-equal-height',
		matchHeight: '.carousel-match-height',
	};

	classes = {
		carouselTransition: `${carouselClassName}--transition`,
		carouselSingle: `${carouselClassName}--single`,
		nextSlide: `${slideClassName}--next`,
		prevSlide: `${slideClassName}--prev`,
		widerVideo: `${thirdPartyVideoClassName}--wider`,
		tallerVideo: `${thirdPartyVideoClassName}--taller`,
		youtubeVideo: `${selectors.thirdPartyVideo}--youtube`,
		youkuVideo: `${selectors.thirdPartyVideo}--youku`,
		flickity: `${carouselClassName}--flickity`,
		ctaBlockEmpty: 'CtaBlock--empty',
		slideWithEmptyCTABlock: `${slideClassName}--emptyCTABlock`,
	};
};

setup();

export const a11yHideSlide = slide => {
	$(slide).attr('aria-hidden', 'true').find('a', 'button').attr('tabindex', -1);
};

export const a11yShowSlide = slide => {
	$(slide).attr('aria-hidden', 'false').find('a', 'button').attr('tabindex', 0);
};
const getOptions = ($carouselContainer, activeIndex) => {
	return Object.assign(
		{
			cellAlign: 'center',
			pageDots: false,
			prevNextButtons: false,
			rightToLeft: getRTL(),
			imagesLoaded: true,
			wrapAround: !!(getSlidesLength($carouselContainer) > 2),
			selectedAttraction: 0.05,
			friction: 0.8,
			fade: false,
			initialIndex: activeIndex,
		},
		overrideOptions
	);
};

const getSlidesLength = $carouselContainer => {
	const $list = $carouselContainer.children(selectors.carouselList);
	const $slides = $list.children(selectors.carouselSlides);

	return $slides.length;
};

const isCarouselEqualHeight = $carouselContainer => {
	return $carouselContainer.find(selectors.equalHeight).length > 0;
};

const fixCardsHeight = ($carouselContainer, action = 'off') => {
	if (action === 'on') {
		let maxMatchHeight = 0;
		$carouselContainer
			.find(`${selectors.equalHeight} ${selectors.matchHeight}`)
			.each((_i, element) => {
				maxMatchHeight = Math.max(maxMatchHeight, element.offsetHeight);
			});
		$carouselContainer
			.find(`${selectors.equalHeight} ${selectors.matchHeight}`)
			.css('height', maxMatchHeight);
	} else {
		$carouselContainer
			.find(`${selectors.equalHeight} ${selectors.matchHeight}`)
			.css('height', '');
	}
	const height = action === 'on' ? '100%' : '';
	$carouselContainer.find(selectors.equalHeight).css('height', height);
};

const initCarousel = ($carouselContainer, activeIndex) => {
	const $list = $carouselContainer.children(selectors.carouselList);
	const hasVideos = !!($list.find(selectors.thirdPartyVideo).length > 0);
	const $slides = $list.children(selectors.carouselSlides);
	const slidesLength = getSlidesLength($carouselContainer);
	const nextButton = $carouselContainer.find(selectors.carouselNextButton);
	const prevButton = $carouselContainer.find(selectors.carouselPrevButton);
	const $carousel = $list.get(0);

	$carouselContainer.addClass(classes.flickity);

	// Fix height when there's only a title or a short description for all slides
	$slides.find('.CtaBlock').each((i, ctaBlock) => {
		const $ctaBlock = $(ctaBlock);
		const isEmpty = $.trim($ctaBlock.html()) == '';
		if (isEmpty) {
			$ctaBlock.addClass(classes.ctaBlockEmpty);
			$($slides[i]).addClass(classes.slideWithEmptyCTABlock);
		}
		$ctaBlock.css({ minHeight: '40px' });
	});

	if (hasVideos) {
		fixVideo($list);
		initializeYoutube($list);
	}

	attachAnalyticsEvents({ $carouselContainer, $slides });

	const flickity = new Flickity($carousel, getOptions($carouselContainer, activeIndex));

	inlineCarouselSlides.push({ flickity, $carouselContainer });
	fixCardsHeight($carouselContainer, 'on');
	$carousel.removeAttribute('tabIndex');

	let userCausedAction = false;
	nextButton.on('click.flickity', event => {
		userCausedAction = true;
		flickity.next();
		if (!isKeyEvent(event)) {
			nextButton.blur();
		} else if (slidesLength === 2) {
			prevButton.focus();
		}
	});
	prevButton.on('click.flickity', event => {
		userCausedAction = true;
		flickity.previous();
		if (!isKeyEvent(event)) {
			prevButton.blur();
		} else if (slidesLength === 2) {
			nextButton.focus();
		}
	});

	const addTransition = () => {
		$carouselContainer.addClass(classes.carouselTransition);

		// Remove unnecessary navigation buttons for two slides
		if (slidesLength === 2) {
			if (flickity.selectedIndex == 0) {
				prevButton.attr('hidden', 'true');
				nextButton.removeAttr('hidden');
			} else {
				nextButton.attr('hidden', 'true');
				prevButton.removeAttr('hidden');
			}
		}
	};
	const removeTransition = () => $carouselContainer.removeClass(classes.carouselTransition);
	const debouncedRemoveTransition = _debounce(removeTransition, 1200);

	flickity.on('staticClick', (e, p, cell, index) => flickity.select(index));

	flickity.on('dragStart', () => {
		userCausedAction = true;
	});

	flickity.on('select', () => {
		addTransition();
		debouncedRemoveTransition();

		// Specify next and previous slides
		$slides.each((i, slide) =>
			$(slide).removeClass(`${classes.nextSlide} ${classes.prevSlide}`)
		);
		const selectedIndex = flickity.selectedIndex;
		const cells = flickity.getCellElements();
		const prevIndex = selectedIndex == 0 ? slidesLength : selectedIndex - 1;
		const nextIndex = selectedIndex == slidesLength ? 0 : selectedIndex + 1;
		const prevCell = cells[prevIndex];
		const nextCell = cells[nextIndex];

		$(nextCell).addClass(classes.nextSlide);
		$(prevCell).addClass(classes.prevSlide);

		if (userCausedAction) {
			userCausedAction = false;
			triggerAnalyticsEvent('carousel.transition', {
				element: $carouselContainer,
				slide: $(flickity.selectedElement),
			});
		}
	});

	flickity.on('settle', () => {
		// Hide cells from keyboard users and screen readers
		const cells = flickity.getCellElements();
		cells.forEach(a11yHideSlide);
		a11yShowSlide(cells[flickity.selectedIndex]);
	});

	if (hasVideos) {
		$(window).on(
			'resize.flickity',
			_debounce(() => fixVideo($list), 100)
		);
	}
};

const initSingle = $carousel => {
	const $list = $carousel.children(`.${carouselClassName}-list`);
	const hasVideos = !!($list.find(selectors.thirdPartyVideo).length > 0);
	if (hasVideos) {
		fixVideo($list);
		initializeYoutube($list);
	}
	if (indexedHashProperty) {
		const $scrollToElements = $(`#${indexedHashProperty.id}`);
		if ($scrollToElements.length) {
			const $scrollToElement = $scrollToElements.first();
			// Check if the scroll to element is a parent of current carousel
			if ($carousel.parents(`#${indexedHashProperty.id}`).length) {
				onLoadComplete(() => {
					scrollToElement($scrollToElement);
				});
			}
		}
	}
};

const initializeYoutube = $container => {
	$container.find(classes.youtubeVideo).each((i, element) => {
		const $element = $(element);
		const $div = $element.find('div');
		const divId = $div.attr('id');
		const videoId = $div.data('video-id');

		ytReady(() => {
			youtubePlayers[divId] = new YT.Player(divId, {
				host: 'https://www.youtube-nocookie.com',
				playerVars: {
					enablejsapi: 1,
					origin: document.domain,
					rel: 0,
				},
				width: '100%',
				height: '100%',
				videoId: videoId,
				events: {
					onReady: attachYouTubeAnalyticsEvents,
				},
			});
		});
	});
};

const fixVideo = list => {
	const firstContainer = list.find(selectors.carouselSlides).first();
	const videos = list.find(selectors.thirdPartyVideo);
	videos.css({
		height: firstContainer.width() * (1 / videoAspectRatio),
		width: '100%',
	});
};
const defaultSupportedBreakpoints = ['small', 'medium', 'large', 'xlarge'];
let supportedBreakpoints = defaultSupportedBreakpoints;

const update = () => {
	inlineCarouselSlides.forEach(({ flickity, $carouselContainer }) => {
		if (supportedBreakpoints.includes(getBreakpoint())) {
			fixCardsHeight($carouselContainer, 'off');
			flickity.reloadCells();
			fixCardsHeight($carouselContainer, 'on');
		} else {
			fixCardsHeight($carouselContainer, 'off');
		}
	});
};

export default function initCarousels(
	carouselClassNameOverride,
	supportedBreakpointsOverride,
	optionsOverride
) {
	overrideOptions = optionsOverride || {};
	if (carouselClassNameOverride) {
		carouselClassName = carouselClassNameOverride;
	} else {
		carouselClassName = defaultCarouselClassName;
	}
	setup();
	if (supportedBreakpointsOverride) {
		supportedBreakpoints = supportedBreakpointsOverride;
	} else {
		supportedBreakpoints = defaultSupportedBreakpoints;
	}
	$(selectors.carouselContainer).each((i, carouselContainer) => {
		const $carouselContainer = $(carouselContainer);

		let activeIndex = 0;
		if (indexedHashProperty) {
			const $scrollToElements = $(`#${indexedHashProperty.id}`);
			if ($scrollToElements.length) {
				const $scrollToElement = $scrollToElements.first();
				// Check if the current carousel is a child of the scroll to element
				if ($carouselContainer.parents(`#${indexedHashProperty.id}`).length) {
					// Set requested page to be the active one
					activeIndex = indexedHashProperty.index - 1;
					onLoadComplete(() => {
						scrollToElement($scrollToElement);
					});
				}
			}
		}

		if (supportedBreakpoints.includes(getBreakpoint())) {
			initCarousel($carouselContainer, activeIndex);
		}
		// Flickity fires select on resize, but not settle.
		// As select removes chevrons (on transition) this adds them back.
		$(window).on(
			'resize.flickity',
			_debounce(() => $carouselContainer.removeClass(classes.carouselTransition), 200)
		);

		if (isCarouselEqualHeight($carouselContainer)) {
			$(window).on('resize', _debounce(update, 100));
		}
	});

	$(`.${classes.carouselSingle}--js`).each((i, singleCarousel) => {
		initSingle($(singleCarousel));
	});
	return inlineCarouselSlides;
}
